/**
 * Tiny helpers to persist list UI state in the URL (Back / Refresh).
 * Keep keys short; omit defaults to keep URLs clean.
 */

export const readQuery = (search) => new URLSearchParams(search || ``)

export const getQueryString = (params) => {
  const s = params.toString()
  return s
}

/** Build `?a=1&b=2` or `` from a plain object; skips null/undefined/'' */
export const buildSearchFromObject = (obj = {}) => {
  const params = new URLSearchParams()
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === ``) return
    params.set(key, String(value))
  })
  return params.toString()
}

/**
 * Replace current path search if different.
 * @param {object} history - react-router history
 * @param {string} pathname
 * @param {string} nextSearch - without leading `?`
 * @param {string} currentSearch - location.search (with or without `?`)
 */
export const replaceSearchIfChanged = (
  history,
  pathname,
  nextSearch,
  currentSearch,
) => {
  const current = (currentSearch || ``).startsWith(`?`)
    ? currentSearch.slice(1)
    : currentSearch || ``
  if (current === (nextSearch || ``)) return
  history.replace(nextSearch ? `${pathname}?${nextSearch}` : pathname)
}

const LIST_RETURN_PREFIX = `btms:listReturn:`

const rememberListReturn = (listPathname, fullUrl) => {
  try {
    sessionStorage.setItem(`${LIST_RETURN_PREFIX}${listPathname}`, fullUrl)
  } catch (_) {
    /* ignore quota / private mode */
  }
}

const readRememberedListReturn = (listPathname) => {
  try {
    return sessionStorage.getItem(`${LIST_RETURN_PREFIX}${listPathname}`)
  } catch (_) {
    return null
  }
}

/**
 * Open a detail route while remembering the current list URL (path + query)
 * so breadcrumbs / UI "back to list" can restore page & filters — same as
 * browser Back. Works with useListUrlSync / any list that syncs `?page=…`.
 *
 * @param {object} history
 * @param {object} location - react-router location (pathname + search)
 * @param {string} detailPathname - e.g. `/users/abc`
 */
export const pushDetailKeepingList = (history, location, detailPathname) => {
  const listReturn = `${location.pathname}${location.search || ``}`
  rememberListReturn(location.pathname, listReturn)
  history.push({
    pathname: detailPathname,
    state: {
      ...(location.state && typeof location.state === `object`
        ? location.state
        : {}),
      listReturn,
    },
  })
}

/**
 * Resolve where a breadcrumb / sidebar list link should go.
 * Prefers location.state.listReturn for the **same** listPathname, then
 *

 * @param {object} location
 * @param {string} listPathname - e.g. `/users`
 */
export const resolveListReturn = (location, listPathname) => {
  const fromState = location?.state?.listReturn
 
  if (
    typeof fromState === `string` &&
    fromState.startsWith(`/`) &&
    (fromState === listPathname || fromState.startsWith(`${listPathname}?`))
  ) {
    return fromState
  }

  const remembered = readRememberedListReturn(listPathname)
  if (
    typeof remembered === `string` &&
    (remembered === listPathname ||
      remembered.startsWith(`${listPathname}?`))
  ) {
    return remembered
  }

  return listPathname
}
